import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/api';
import Pagination from '../Pagination';
import { MdOutlineBlock } from 'react-icons/md';
import Loader from '../Loader';
import * as XLSX from "xlsx";


const AirtimeRequests = () => {

  const [transaction, setTransaction] = useState([]);
  const [searchAccountNumber, setSearchAccountNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [count, setCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusCode, setStatusCode] = useState('');

  // Convert date to partition format (pYYYYMMDD)
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch airtime transactions
  const getTransactions = useCallback(async (offset, limit) => {
    try {
      let query = `?offset=${offset}&limit=${limit}&service_id=2`

      if (statusCode) {
        query += `&status_code=${statusCode}`
      }


      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if endDate is before startDate
        if (end < start) {
          alert('End date cannot be earlier than start date');
          setLoading(false);
          return;
        }
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 32) {
          alert('Date range cannot exceed 32 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);

        // Handle case when start and end are the same
        if (diffDays === 0) {
          let current = new Date(startDate);
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        } else {
          while (current <= end) {
            current.setDate(current.getDate() + 1);
            partitions.push(formatDateToPartition(current));
          }
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      if (searchAccountNumber) query += `&account_number=${searchAccountNumber}`;

      const response = await axios.get(`/batch_request/${query}`);
      setTransaction(response.data.items);
      setCount(response.data.count)
      setTotal(response.data.total)
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      if (!error.response) {
        setError("Network error! Check your connection.");
      } else if (error.response.status) {
        setError(error.response.data.detail)
      } else if (error.response.status >= 500) {
        setError("Server error! Please try again later.");
      } else {
        setError("An unexpected error occurred!");
      }
    }
  }, [startDate, endDate, searchAccountNumber, statusCode]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getTransactions((currentPage - 1) * recordsPerPage, recordsPerPage, searchAccountNumber);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, getTransactions, searchAccountNumber]);

  const handleExport = () => {
    const workbook = XLSX.utils.book_new();
    const excelData = transaction.map((row) => {
      return {
        "Service Account": row.client_id,
        "Account Number": row.account_number,
        "Amount": row.amount,
        "Service": row.service_type,
        "Scope": row.scope,
        "Date Created": new Date(row.date_created).toLocaleString("en-CA", { hour12: false }), 
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Requests");
    XLSX.writeFile(workbook, "failed_requests.xlsx");
  };

  return (
    <section className='mx-auto p-4'>
      <h4><b>Airtime Requests</b></h4>
      <div className="bg-white rounded-lg p-4 w-full flex-1 min-w-0 mt-2">
        <div>
          <form className='flex items-center flex-wrap'>
            <div className='p-1 w-full lg:w-1/5'>
              <input
                type='search'
                className='px-3 py-1  w-full border bg-[#eef1ff] border-slate-300 rounded-md'
                placeholder='Search account number'
                value={searchAccountNumber}
                onChange={(e) => setSearchAccountNumber(e.target.value)}
              />
            </div>
            <label className='p-1 w-full lg:w-1/5' htmlFor="">
              <span className='hidden'>Start Date</span>
              <input 
                type="date"
                className='px-2 py-1 w-full border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                value={startDate}
                onChange={(e) => {setStartDate(e.target.value)}}
              />
            </label>
            <label className='p-1 w-full lg:w-1/5' htmlFor="">
              <span className='hidden'>End Date</span>
              <input 
                type="date" 
                className='px-2 py-1  w-full border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                value={endDate}
                onChange={(e) => {setEndDate(e.target.value)}}
              />
            </label>
            <div className='p-1 w-full lg:w-1/5'>
              <select
                className='px-2 py-1.5 w-full border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                value={statusCode}
                onChange={(e) => {setStatusCode(e.target.value)}}
              >
                <option value=''>Status</option>
                <option value='1'>Success</option>
                <option value='2'>Reversed</option>
                <option value='4'>Failed</option>
              </select>
            </div>
            <button className='bg-[#134463] text-white px-4 py-1.5 rounded-lg hover:bg-[#165277]' onClick={handleExport}>Export</button>
          </form>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#e9f3f9] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#e9f3f9] grid place-items-center h-[70vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          <div className='overflow-x-auto rounded-lg'>
            {transaction.length > 0
              ? (
                <table className='w-full text-left table-auto'>
                  <thead>
                    <tr className='border-b border-slate-500'>
                      <th className='p-2'>Service Account</th>
                      <th className='p-2'>Account Number</th>
                      <th className='p-2'>Amount</th>
                      <th className='p-2'>Service</th>
                      <th className='p-2'>Status</th>
                      <th className='p-2'>Scope</th>
                      <th className='p-2'>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transaction.map((trans) => (
                      <tr key={trans.id}>
                        <td className='p-2 '>{trans.client_id} - <span className="text-[#2A8B4F]">Live</span></td>
                        <td className='p-2'>{trans.account_number}</td>
                        <td className='p-2'>{trans.amount}</td>
                        <td className='p-2'>{trans.service_type}</td>
                        <td className='p-2'>{trans.status_code === 1 ? (
                            <span className='bg-[#D9F8EB] text-[#2A8B4F] px-4 py-1 rounded-md font-bold'>Success</span>
                          ) : trans.status_code === 0 ? (
                            <span className='bg-[#D9E7FA] text-[#215CB1] px-4 py-1 rounded-md font-bold'>Processing</span>
                          ) : trans.status_code === 2 ? (
                            <span className='bg-slate-200 text-slate-500 px-4 py-1 rounded-md font-bold'>Reversed</span>
                          ) : (
                            <span className='bg-[#FDE6D8] text-[#AC5A2B] px-4 py-1 rounded-md font-bold'>Failed</span>
                          )}
                        </td>
                        <td className='p-2'>{trans.scope}</td>
                        <td className='p-2'>{new Date(trans.date_created).toLocaleString("en-CA", { hour12: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='bg-[#e9f3f9] text-[#134463] grid place-items-center h-[70vh]'>
                  <div className='grid place-items-center p-4'>
                    <h3><MdOutlineBlock /></h3>
                    <h4>No Data</h4>
                  </div>
                </div>
              )}
          </div>
        )}
        <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
          <div>
            <span className='mr-2'>Records per page:</span>
            <select
              className='px-3 py-1 border bg-[#e9f3f9] border-slate-300 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
            >
              <option value='10'>10</option>
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='75'>75</option>
              <option value='100'>100</option>
            </select>
          </div>
          <h5 className='text-[#165277] font-bold my-4'>Showing {count} / {total} Airtime requests</h5>
          <Pagination
            nPages={Math.ceil(total / recordsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </section>
  )
}

export default AirtimeRequests
